import { Badge, Button, Progress, Tag } from 'antd';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useMemo, useState } from 'react';

import { Card } from 'app/components/layout/Card';
import { useCountries } from 'app/hooks/data/useCountries';

import 'app/typings';

import type { ISalesResponse } from 'app/typings/sales';
import { differenceInHours, format, isAfter } from 'app/utils/dayjs';

import { SaleEditModal } from '../SaleEditModal/SaleEditModal';

import './SaleInfo.scss';

type SaleInfoProps = {
  sale: ISalesResponse;
};

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault('Europe/Paris');

export const SaleInfo = ({ sale }: SaleInfoProps) => {
  const { startDate, endDate, bookableDays } = sale;
  const { data: countries } = useCountries();
  const country = countries?.find((c) => c.id === sale.countryId);

  const [openEditModal, setOpenEditModal] = useState<boolean>(false);

  const { isOpen, isClosed, progress } = useMemo(() => {
    const saleLength = differenceInHours(
      new Date(endDate),
      new Date(startDate)
    );
    const saleProgress = differenceInHours(new Date(), new Date(startDate));

    return {
      isOpen: isAfter(new Date(), new Date(startDate)),
      isClosed: isAfter(new Date(), new Date(endDate)),
      progress: (100 * saleProgress) / saleLength,
    };
  }, [startDate, endDate]);

  return (
    <div className="sale-info">
      <Card
        title={`Current Sale ${sale.open ? '' : '(FORCED CLOSED)'}`}
        className="sale-info__timeline"
        actions={[<Button onClick={() => setOpenEditModal(true)}>Edit</Button>]}
      >
        <div className="sale-info__progress">
          <Badge status={isOpen ? 'success' : 'default'} />
          <Progress
            status="success"
            strokeWidth={4}
            percent={progress}
            showInfo={false}
          />
          <Badge status={isClosed ? 'success' : 'default'} />
        </div>
        <div className="sale-info__labels">
          <p>Open</p>
          <p>Close</p>
        </div>
        <div className="sale-info__dates">
          <p>
            {dayjs(startDate)
              .tz(country?.timezone || 'Europe/Paris')
              .format('dddd DD MMMM • HH:mm')}
          </p>
          <p>
            {dayjs(endDate)
              .tz(country?.timezone || 'Europe/Paris')
              .format('dddd DD MMMM • HH:mm')}
          </p>
        </div>
        <div className="sale-info__section">
          <h4>Bookable days</h4>
          <div className="sale-info__bookable-days">
            {bookableDays.map((day) => (
              <Tag key={day}>{format(new Date(day), 'ddd DD MMM')}</Tag>
            ))}
          </div>
        </div>
      </Card>
      {openEditModal && (
        <SaleEditModal
          sale={sale}
          isOpen={openEditModal}
          onClose={() => setOpenEditModal(false)}
        />
      )}
    </div>
  );
};
